/**
 * StaffingTrendCard — Monthly HPRD (nursing hours per resident day) line chart.
 *
 * Shows facility staffing trend compared to state average, CMS minimum, and
 * the MDS acuity-expected HPRD for the selected model (anticipated/hybrid).
 * Adapted from eliotnest_core StaffingTrendCard (prosecution baselines removed).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import api from '../../api/axios';
import { getStateMinHprd } from '../../config/stateMinHprd';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface StaffingMonth {
  year: number;
  month: number;
  month_label: string;
  days_in_month: number;
  avg_census: number | null;
  hprd: number | null;
  rn_hppd: number | null;
  ant_expected_hprd: number | null;
  hybrid_expected_hprd: number | null;
}

interface StaffingTimelineResponse {
  ccn: string;
  state: string | null;
  timeline: StaffingMonth[];
  baselines: {
    state_avg: number | null;
    national_avg: number | null;
    cms_minimum?: number;
  };
  summary: {
    months_with_data: number;
    facility_avg_hprd: number | null;
    state_avg_hprd: number | null;
  };
}

interface Props {
  ccn: string;
  startYear?: number;
  endYear?: number;
  model?: 'anticipated' | 'hybrid';
}

const StaffingTrendCard: React.FC<Props> = ({ ccn, startYear, endYear, model = 'anticipated' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StaffingTimelineResponse | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, number> = {};
      if (startYear) params.start_year = startYear;
      if (endYear) params.end_year = endYear;
      const res = await api.get(`/facilities/${ccn}/staffing-timeline/`, { params });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching staffing timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [ccn, startYear, endYear]);

  useEffect(() => {
    if (ccn) fetchData();
  }, [ccn, fetchData]);

  if (loading) {
    return (
      <Box sx={{ px: 1.5, py: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      </Box>
    );
  }

  if (!data || data.timeline.length === 0) {
    return (
      <Box sx={{ px: 1.5, py: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" fontWeight={600}>Staffing Trend</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No staffing data available</Typography>
      </Box>
    );
  }

  const validData = data.timeline.filter(d => d.hprd !== null && d.hprd > 0);
  if (validData.length === 0) return null;

  const { baselines, summary } = data;
  const stateAvg = baselines.state_avg;
  const stateMin = getStateMinHprd(data.state);

  // Expected HPRD from the selected acuity model (per valid-PDPM resident-day)
  const expectedKey = model === 'hybrid' ? 'hybrid_expected_hprd' : 'ant_expected_hprd';
  const expectedLabel = model === 'hybrid' ? 'Expected (Hybrid)' : 'Expected (Anticipated)';
  const expectedData = data.timeline.filter(d => d[expectedKey] !== null && (d[expectedKey] as number) > 0);
  const expectedByMonth = new Map(expectedData.map(d => [d.month_label, d[expectedKey] as number]));

  // Chart dimensions
  const svgWidth = 900;
  const svgHeight = 260;
  const padding = { left: 50, right: 100, top: 15, bottom: 30 };
  const plotWidth = svgWidth - padding.left - padding.right;
  const plotHeight = svgHeight - padding.top - padding.bottom;

  // Scale — time-based so axis extends to endYear even without data
  const allValues = validData.map(d => d.hprd as number);
  expectedData.forEach(d => allValues.push(d[expectedKey] as number));
  if (stateAvg) allValues.push(stateAvg);
  if (stateMin != null) allValues.push(stateMin);
  const minVal = Math.min(...allValues) * 0.85;
  const maxVal = Math.max(...allValues) * 1.1;

  const firstDataYear = validData[0].year;
  const firstDataMonth = validData[0].month;
  const requestedEndYear = endYear || validData[validData.length - 1].year;
  // Total months from first data point to Dec of endYear
  const totalMonths = (requestedEndYear - firstDataYear) * 12 + (12 - firstDataMonth) + 1;

  const monthToX = (year: number, month: number) => {
    const offset = (year - firstDataYear) * 12 + (month - firstDataMonth);
    return padding.left + (offset / Math.max(totalMonths - 1, 1)) * plotWidth;
  };

  const scaleY = (v: number) => padding.top + plotHeight - ((v - minVal) / (maxVal - minVal)) * plotHeight;

  // Facility line path
  const facilityPath = validData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${monthToX(d.year, d.month)} ${scaleY(d.hprd as number)}`).join(' ');

  // Expected (acuity) line path
  const expectedPath = expectedData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${monthToX(d.year, d.month)} ${scaleY(d[expectedKey] as number)}`).join(' ');
  const expectedColor = '#7b1fa2';

  // Year markers on x-axis — one per year, from first year to endYear
  const yearMarkers: { x: number; label: string }[] = [];
  for (let y = firstDataYear; y <= requestedEndYear; y++) {
    yearMarkers.push({ x: monthToX(y, 1), label: String(y) });
  }

  // Facility color — red if below CMS, orange if below state avg, green if above
  const facilityAvg = summary.facility_avg_hprd || 0;
  const facilityColor = stateAvg && facilityAvg < stateAvg ? '#ed6c02' : '#2e7d32';

  return (
    <Box sx={{ px: 1.5, py: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>Staffing Trend</Typography>
          <Typography variant="h6" fontWeight={700}>PBJ Reported Nursing Hours Per Resident Day (HPRD)</Typography>
          <Typography variant="caption" color="text.secondary">
            Monthly average — RN + LPN + CNA hours per patient day
            {expectedData.length > 0 && ` · dashed purple = MDS acuity-expected HPRD (${model === 'hybrid' ? 'Hybrid' : 'Anticipated'} model)`}
          </Typography>
        </Box>
        {summary.facility_avg_hprd && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Facility Average</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: facilityColor }}>
              {summary.facility_avg_hprd.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>

      <svg width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid meet">
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
          const val = minVal + (maxVal - minVal) * pct;
          const y = scaleY(val);
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth={1} />
              <text x={padding.left - 6} y={y} textAnchor="end" alignmentBaseline="middle" fontSize={10} fill="#999">
                {val.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* State average line */}
        {stateAvg && (
          <>
            <line
              x1={padding.left} y1={scaleY(stateAvg)}
              x2={svgWidth - padding.right} y2={scaleY(stateAvg)}
              stroke="#1565c0" strokeWidth={1.5} strokeDasharray="4,4"
            />
            <text x={svgWidth - padding.right + 4} y={scaleY(stateAvg)} fontSize={10} fill="#1565c0" alignmentBaseline="middle">
              {data.state} Avg ({stateAvg.toFixed(2)})
            </text>
          </>
        )}

        {/* State minimum (statutory floor) line */}
        {stateMin != null && (
          <>
            <line
              x1={padding.left} y1={scaleY(stateMin)}
              x2={svgWidth - padding.right} y2={scaleY(stateMin)}
              stroke="#d32f2f" strokeWidth={1.5} strokeDasharray="2,3"
            />
            <text x={svgWidth - padding.right + 4} y={scaleY(stateMin)} fontSize={10} fill="#d32f2f" alignmentBaseline="middle">
              {data.state} Min ({stateMin.toFixed(2)})
            </text>
          </>
        )}

        {/* Expected (acuity model) line */}
        {expectedData.length > 0 && (
          <>
            <path d={expectedPath} fill="none" stroke={expectedColor} strokeWidth={2} strokeDasharray="6,3" />
            <text
              x={monthToX(expectedData[expectedData.length - 1].year, expectedData[expectedData.length - 1].month) + 8}
              y={scaleY(expectedData[expectedData.length - 1][expectedKey] as number)}
              fontSize={10}
              fill={expectedColor}
              fontWeight={700}
              alignmentBaseline="middle"
            >
              {expectedLabel}
            </text>
          </>
        )}

        {/* Facility line */}
        <path d={facilityPath} fill="none" stroke={facilityColor} strokeWidth={2.5} />

        {/* Facility label at end */}
        {validData.length > 0 && (
          <text
            x={monthToX(validData[validData.length - 1].year, validData[validData.length - 1].month) + 8}
            y={scaleY(validData[validData.length - 1].hprd as number)}
            fontSize={10}
            fill={facilityColor}
            fontWeight={700}
            alignmentBaseline="middle"
          >
            Facility
          </text>
        )}

        {/* Hover dots + tooltips */}
        {validData.map((d, i) => {
          const x = monthToX(d.year, d.month);
          const y = scaleY(d.hprd as number);
          return (
            <Tooltip
              key={i}
              title={
                <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  <Box fontWeight={600} mb={0.5}>{MONTH_LABELS[d.month - 1]} {d.year}</Box>
                  <Box>HPRD: {(d.hprd as number).toFixed(2)}</Box>
                  {expectedByMonth.has(d.month_label) && (
                    <Box>{expectedLabel}: {(expectedByMonth.get(d.month_label) as number).toFixed(2)}</Box>
                  )}
                  {d.avg_census && <Box>Census: {d.avg_census}</Box>}
                </Box>
              }
              arrow
            >
              <circle cx={x} cy={y} r={3} fill={facilityColor} opacity={0.7} style={{ cursor: 'pointer' }} />
            </Tooltip>
          );
        })}

        {/* Year labels */}
        {yearMarkers.map((m, i) => (
          <text key={i} x={m.x} y={svgHeight - 6} textAnchor="start" fontSize={11} fill="#666" fontWeight={600}>
            {m.label}
          </text>
        ))}
      </svg>
    </Box>
  );
};

export default StaffingTrendCard;
